import Multiselect from "multiselect-react-dropdown";
import React, { useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import {
    FaChevronRight,
    FaChevronDown,
    FaCheckCircle,
    FaFolderOpen,
    FaEye,
    FaPlus,
    FaEdit,
    FaTrash,
    FaGlobe,
    FaCheckDouble
} from "react-icons/fa";
import { ensureArray } from "../../../Helpers/utils";
import { MultiSelectModules } from "../helpers/aclHelper";
import PermissionButton from "./PermissionButton";

const SELECT_ALL = { name: "Select All", id: "__all__" };

// multiselect-react-dropdown always renders its full <li> option list into the DOM
// (just CSS-hidden when closed) regardless of open/closed state -- with a dialer that
// has thousands of CLI numbers, "Select All" was freezing the tab. Cap what actually
// gets rendered and let users search past the cap instead of always mounting everything.
const MAX_VISIBLE_OPTIONS = 200;

const style = {
    searchBox: {
        border: '1px solid #dde2e8',
        borderRadius: '8px',
        minHeight: '38px',
        maxHeight: '43px',
        padding: '5px 14px',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#fff'
    },
    option: {
        color: '#33404f',
        background: 'white'
    },
    optionContainer: {
        borderRadius: '8px',
        boxShadow: '0 6px 16px rgba(30, 58, 95, 0.12)',
        maxHeight: '250px',
        overflowY: 'auto'
    }
};

const AclTable = ({
    loading,
    filteredModules,
    permissions,
    expandedModules,
    moduleListingData,
    multiSelectOptions,
    togglePermission,
    toggleAll,
    isAllChecked,
    toggleExpand,
    setModuleListingData,
    readOnly = false
}) => {

    const [searchTerms, setSearchTerms] = useState({});

    const getOptions = (moduleName) => {
        const original = ensureArray(multiSelectOptions?.[moduleName]);

        // Manage Dialers has no "Select All" -- a dialer is exclusively allocated to
        // one active ACL at a time (see getUnallocatedDialerList on the backend), so
        // dialers must be picked one by one, deliberately, instead of bulk-selected.
        if (moduleName === "Manage Dialers") {
            return original;
        }

        return [SELECT_ALL, ...original];
    };

    // Caps + searches an already-computed options list -- keeps already-selected
    // items visible even past the cap so checked items never silently vanish from view.
    const capAndSearch = (items, moduleName, selectedIds) => {
        const term = (searchTerms[moduleName] || "").trim().toLowerCase();
        const matches = term
            ? items.filter(item => (item.name || "").toLowerCase().includes(term))
            : items;

        if (matches.length <= MAX_VISIBLE_OPTIONS) return matches;

        const selected = matches.filter(item => selectedIds.includes(item.id));
        const unselected = matches.filter(item => !selectedIds.includes(item.id));
        return [
            ...selected,
            ...unselected.slice(0, Math.max(0, MAX_VISIBLE_OPTIONS - selected.length))
        ];
    };

    // What actually gets handed to <Multiselect> as `options` -- the library renders
    // this full list into the DOM (hidden via CSS) even while the dropdown is closed,
    // so this must stay bounded no matter how large the underlying dataset is.
    // Select-all state/actions still use the full getOptions()/multiSelectOptions data,
    // so capping here never affects correctness -- only what's visible to browse.
    const getDisplayOptions = (moduleName) => {
        const selectedIds = ensureArray(moduleListingData?.[moduleName]).map(r => r?.id ?? r);

        const options = getOptions(moduleName);
        const rest = options.filter(item => item.id !== "__all__");
        const capped = capAndSearch(rest, moduleName, selectedIds);
        return options.some(item => item.id === "__all__") ? [SELECT_ALL, ...capped] : capped;
    };

    const isOptionsCapped = (moduleName) => {
        const options = getOptions(moduleName).filter(item => item.id !== "__all__");
        return options.length > MAX_VISIBLE_OPTIONS;
    };

    const getSelectedValues = (moduleName) => {

        const selectedItems = ensureArray(
            moduleListingData[moduleName]
        );

        const selectedIds = selectedItems.map(
            r => r?.id ?? r
        );

        // IMPORTANT
        // USE SAME OPTIONS OBJECT REFERENCES
        const options = getOptions(moduleName);

        const actualOptions = options.filter(
            item => item.id !== "__all__"
        );

        const selectedObjects = actualOptions.filter(
            opt => selectedIds.includes(opt.id)
        );

        let finalSelected = [...selectedObjects];

        // GLOBAL SELECT ALL -- not applicable to Manage Dialers, which has no
        // "Select All" option at all (dialers are picked one by one, manually).
        const allSelected =
            moduleName !== "Manage Dialers" &&
            actualOptions.length > 0 &&
            actualOptions.every(opt =>
                selectedIds.includes(opt.id)
            );

        if (allSelected) {
            finalSelected.unshift(SELECT_ALL);
        }

        return finalSelected;
    };

    const handleSelect = (moduleName, selectedList, selectedItem) => {
        const allItems = ensureArray(multiSelectOptions[moduleName]);

        // GLOBAL SELECT ALL
        if (selectedItem.id === "__all__") {
            setModuleListingData(prev => ({
                ...prev,
                [moduleName]: [...allItems]
            }));

            return;
        }

        const cleaned = selectedList.filter(
            item => item.id !== "__all__"
        );

        setModuleListingData(prev => ({
            ...prev,
            [moduleName]: cleaned
        }));
    };

    const handleRemove = (moduleName, selectedList, removedItem) => {
        if (removedItem.id === "__all__") {
            setModuleListingData(prev => ({
                ...prev,
                [moduleName]: []
            }));

            return;
        }

        const cleaned = selectedList.filter(
            i => i.id !== "__all__"
        );

        setModuleListingData(prev => ({
            ...prev,
            [moduleName]: cleaned
        }));
    };

    const handleModuleAllToggle = (m, parentKeys) => {
        const checked = !isAllChecked(m.mname, parentKeys);

        toggleAll(m.mname, parentKeys, checked);

        if (m.children?.length > 0) {
            const childKeys = ['read', 'add', 'edit', 'delete', 'view_permission'];
            m.children.forEach(c => {
                toggleAll(c.moduleName, childKeys, checked);
            });
        }

        if (MultiSelectModules.has(m.mname)) {
            // Manage Dialers must never be bulk-selected -- dialers are picked one by
            // one, manually, from the dropdown. Unchecking "ALL" still clears whatever
            // was selected, same as every other multi-select module.
            if (checked && m.mname !== "Manage Dialers") {
                const allItems = ensureArray(multiSelectOptions[m.mname]);
                setModuleListingData(prev => ({
                    ...prev,
                    [m.mname]: [...allItems]
                }));
            } else if (!checked) {
                setModuleListingData(prev => {
                    const copy = { ...prev };
                    delete copy[m.mname];
                    return copy;
                });
            }
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <div>Loading modules...</div>
            </div>
        );
    }

    return (
        <Table bordered className="align-middle">
            <thead className="table-light">
                <tr>
                    <th><span className="acl-th-inline acl-th-label"><FaFolderOpen size={12} /> Module Name</span></th>
                    <th className="text-center"><span className="acl-th-inline"><FaEye size={12} /> Read</span></th>
                    <th className="text-center"><span className="acl-th-inline"><FaPlus size={12} /> Add</span></th>
                    <th className="text-center"><span className="acl-th-inline"><FaEdit size={12} /> Edit</span></th>
                    <th className="text-center"><span className="acl-th-inline"><FaTrash size={12} /> Delete</span></th>
                    <th className="text-center"><span className="acl-th-inline"><FaGlobe size={12} /> View Permission</span></th>
                    <th className="text-center"><span className="acl-th-inline"><FaCheckDouble size={12} /> All</span></th>
                </tr>
            </thead>

            <tbody>
                {filteredModules.map(m => {
                    const hasChild = m.children?.length > 0;
                    const p = permissions[m.mname] || {};

                    const parentKeys = hasChild
                        ? ['read', 'view_permission']
                        : ['read', 'add', 'edit', 'delete', 'view_permission'];

                    return (
                        <React.Fragment key={m.mid}>

                            {/* PARENT ROW */}
                            <tr>
                                <td className="fw-bold">
                                    <div className="module-cell">
                                        <span
                                            className={`acl-expand-toggle ${hasChild ? '' : 'acl-expand-toggle--empty'}`}
                                            onClick={hasChild ? () => toggleExpand(m.mname) : undefined}
                                        >
                                            {hasChild && (expandedModules[m.mname] ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />)}
                                        </span>
                                        <span className="acl-module-name">{m.mname}</span>
                                    </div>
                                </td>

                                {hasChild ? (
                                    <>
                                        <td className="permission-cell">
                                            <PermissionButton
                                                active={p.read || false}
                                                onClick={() => togglePermission(m.mname, 'read')}
                                                readOnly={readOnly}
                                            />
                                        </td>

                                        <td colSpan={3} className="text-center">
                                            {MultiSelectModules.has(m.mname) && p.view_permission ? (
                                                <>
                                                    {readOnly ? (
                                                        (() => {
                                                            const selectedIds = ensureArray(moduleListingData[m.mname]).map(v => v?.id ?? v);
                                                            const resolvedNames = ensureArray(multiSelectOptions[m.mname])
                                                                .filter(opt => selectedIds.includes(opt.id))
                                                                .map(opt => opt.name);

                                                            return resolvedNames.length > 0 ? (
                                                                <div className="acl-readonly-chips">
                                                                    {resolvedNames.map((name, idx) => (
                                                                        <span key={idx} className="acl-chip">
                                                                            {name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="acl-info-pill">
                                                                    <FaCheckCircle size={11} />
                                                                    No items selected
                                                                </div>
                                                            );
                                                        })()
                                                    ) : (
                                                        <Multiselect
                                                            options={getDisplayOptions(m.mname)}
                                                            selectedValues={getSelectedValues(m.mname)}
                                                            displayValue="name"
                                                            onSelect={(list, item) =>
                                                                handleSelect(m.mname, list, item)
                                                            }
                                                            onRemove={(list, item) =>
                                                                handleRemove(m.mname, list, item)
                                                            }
                                                            onSearch={(term) =>
                                                                setSearchTerms(prev => ({ ...prev, [m.mname]: term }))
                                                            }
                                                            showCheckbox
                                                            hideSelectedList
                                                            placeholder={(() => {
                                                                // "Manage Users" options are actually app Modules, not users --
                                                                // label them as such so the placeholder isn't misleading.
                                                                const label = m.mname === "Manage Users" ? "Module" : m.mname;
                                                                if (moduleListingData[m.mname]?.length > 0) {
                                                                    return `${moduleListingData[m.mname].length} ${label} Selected`;
                                                                }
                                                                if (isOptionsCapped(m.mname)) {
                                                                    return `Select ${label} (showing first ${MAX_VISIBLE_OPTIONS} - type to search more)`;
                                                                }
                                                                return `Select ${label}`;
                                                            })()}
                                                            style={style}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <div className="acl-info-pill">
                                                    <FaCheckCircle size={11} />
                                                    Only have Read and View Permissions
                                                </div>
                                            )}
                                        </td>

                                        <td className="permission-cell">
                                            <PermissionButton
                                                active={p.view_permission || false}
                                                onClick={() => togglePermission(m.mname, 'view_permission')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                    </>
                                ) : (
                                    parentKeys.map(key => (
                                        <td key={key} className="permission-cell">
                                            <PermissionButton
                                                active={p[key] || false}
                                                onClick={() => togglePermission(m.mname, key)}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                    ))
                                )}

                                <td className="permission-cell">
                                    <PermissionButton
                                        active={isAllChecked(m.mname, parentKeys)}
                                        onClick={() => handleModuleAllToggle(m, parentKeys)}
                                        readOnly={readOnly}
                                    />
                                </td>
                            </tr>

                            {/* CHILDREN */}
                            {hasChild && expandedModules[m.mname] &&
                                m.children.map(c => {
                                    const cp = permissions[c.moduleName] || {};

                                    return (
                                        <tr key={`${m.mid}-${c.moduleName}`} className="acl-child-row">
                                            <td className="ps-4">
                                                <span className="acl-child-dot" />
                                                {c.moduleName}
                                            </td>

                                            {['read', 'add', 'edit', 'delete', 'view_permission'].map(key => (
                                                <td key={key} className="permission-cell">
                                                    <PermissionButton
                                                        active={cp[key] || false}
                                                        onClick={() => togglePermission(c.moduleName, key)}
                                                        readOnly={readOnly}
                                                    />
                                                </td>
                                            ))}

                                            <td className="permission-cell">
                                                <PermissionButton
                                                    active={isAllChecked(
                                                        c.moduleName,
                                                        ['read', 'add', 'edit', 'delete', 'view_permission']
                                                    )}
                                                    onClick={() =>
                                                        toggleAll(
                                                            c.moduleName,
                                                            ['read', 'add', 'edit', 'delete', 'view_permission'],
                                                            !isAllChecked(
                                                                c.moduleName,
                                                                ['read', 'add', 'edit', 'delete', 'view_permission']
                                                            )
                                                        )
                                                    }
                                                    readOnly={readOnly}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </Table>
    );
};

export default AclTable;
